/* eslint-disable class-methods-use-this */
import { SearchPredicate } from '@nx-smartmonkey/shared/domain';
import { Requests } from '@nx-smartmonkey/shared/helpers';
import { PaginatedResult } from '@nx-smartmonkey/shared/interfaces';
import { Driver } from '../../domain/driver/Driver';
import { DriverDTO } from '../../domain/driver/DriverDTO';
import { GeoFence } from '../../domain/geoFence/GeoFence';
import { GeoFenceDTO } from '../../domain/geoFence/GeoFenceDTO';
import { Plan } from '../../domain/plan/Plan';
import { PlanDTO } from '../../domain/plan/PlanDTO';
import { PlanFlat } from '../../domain/plan/PlanFlat';
import { PlanFlatDTO } from '../../domain/plan/PlanFlatDTO';
import { PlanStatus } from '../../domain/plan/PlanStatus';
import { PlansCalendar, PlansCalendarDTO } from '../../domain/plan/PlansCalendar';
import { Project } from '../../domain/project/Project';
import { Stop } from '../../domain/stop/Stop';
import { StopDTO } from '../../domain/stop/StopDTO';
import { DriverMapper } from '../../mappers/driver/DriverMapper';
import { GeoFenceMapper } from '../../mappers/geoFence/GeoFenceMapper';
import { PlanFlatMapper } from '../../mappers/plan/PlanFlatMapper';
import { PlanMapper } from '../../mappers/plan/PlanMapper';
import { StopMapper } from '../../mappers/stop/StopMapper';
import { SessionRepository } from '../session/SessionRepository';
import { PlanOptimizationData, PlanRepository } from './PlanRepository';

export class PlanRepositoryApi implements PlanRepository {
  constructor(private requests: Requests, private sessionRepository: SessionRepository) {}

  async fetch({ planId }: { planId: string }): Promise<PlanFlat> {
    const response = await this.requests.get({
      uri: `/v2/plan/${planId}/flat`,
      params: {},
      headers: this.sessionRepository.getAuthenticationHeader(),
    });

    const plan = response.data as PlanFlatDTO;

    return PlanFlatMapper.toDomain(plan);
  }

  async search({
    projectId,
    offset,
    limit,
    sort_by,
    sort_direction,
    predicates = [],
  }: {
    projectId: string;
    offset?: number;
    limit?: number;
    sort_by?: string;
    sort_direction?: string;
    predicates?: Array<SearchPredicate>;
  }): Promise<PaginatedResult<PlanFlat>> {
    const response = await this.requests.post({
      uri: `/v2/plans/search?project_id=${projectId}`,
      data: {
        offset,
        limit,
        predicates: predicates.map((p) => p.getValue()),
        ...(sort_by !== undefined ? { sort_by } : {}),
        ...(sort_direction !== undefined ? { sort_direction } : {}),
      },
      headers: this.sessionRepository.getAuthenticationHeader(),
    });

    const data = response.data as PaginatedResult<PlanDTO>;

    return {
      ...data,
      docs: data.docs.map(PlanFlatMapper.toDomain),
    };
  }

  async fetchFlatPlans({
    projectId,
    predicates = [],
  }: {
    projectId: string;
    predicates?: Array<SearchPredicate>;
  }): Promise<Array<PlanFlat>> {
    const response = await this.requests.post({
      uri: `/v2/plans/flat?project_id=${projectId}`,
      data: {
        predicates: predicates!.map((p) => p.getValue()),
      },
      headers: this.sessionRepository.getAuthenticationHeader(),
    });

    const data = response.data as Array<PlanDTO>;

    return data.map(PlanFlatMapper.toDomain);
  }

  async create({ plan, project }: { plan: Plan; project: Project }): Promise<Plan> {
    const response = await this.requests.post({
      uri: `/v2/plan?project_id=${plan.project_id}`,
      data: PlanMapper.toRepository(plan),
      headers: this.sessionRepository.getAuthenticationHeader(),
    });

    const data = response.data as PlanDTO;

    return PlanMapper.toDomain(data, project.custom_fields!);
  }

  async createWithAssets({ plan, project }: { plan: Plan; project: Project }): Promise<Plan> {
    const response = await this.requests.post({
      uri: `/v2/plan?project_id=${plan.project_id}`,
      data: {
        ...PlanMapper.toRepository(plan),
        ...(plan.stops.length > 0 ? { stops: plan.stops.map((stop) => StopMapper.toRepository(stop)) } : {}),
        ...(plan.drivers.length > 0
          ? { drivers: plan.drivers.map((driver) => DriverMapper.toRepository(driver)) }
          : {}),
      },
      headers: this.sessionRepository.getAuthenticationHeader(),
    });

    const data = response.data as PlanDTO;

    return PlanMapper.toDomain(data, project.custom_fields!);
  }

  async save({ plan, hasTraffic }: { plan: PlanFlat; hasTraffic: boolean }): Promise<PlanFlat> {
    const response = await this.requests.put({
      uri: `/v2/plan/${plan.id}/flat`,
      data: PlanFlatMapper.toRepository(plan, hasTraffic),
      headers: this.sessionRepository.getAuthenticationHeader(),
    });

    const updatedPlan = response.data as PlanDTO;

    return PlanFlatMapper.toDomain(updatedPlan);
  }

  async updateGeoFences({ plan, geo_fences }: { plan: PlanFlat; geo_fences: GeoFence[] }): Promise<PlanFlat> {
    const response = await this.requests.put({
      uri: `/v2/plan/${plan.id}/flat`,
      data: {
        geo_fences: geo_fences.map((geoFence) => geoFence.id),
      },
      headers: this.sessionRepository.getAuthenticationHeader(),
    });

    const updatedPlan = response.data as PlanDTO;

    return PlanFlatMapper.toDomain(updatedPlan);
  }

  async updateFlatPlansStatus({
    flatPlans,
    status,
  }: {
    flatPlans: Array<PlanFlat>;
    status: PlanStatus;
  }): Promise<Array<PlanFlat>> {
    const requests = flatPlans.map((flatPlan) => {
      return {
        method: this.requests.put.bind(this.requests),
        params: {
          uri: `/v2/plan/${flatPlan.id}/flat`,
          data: {
            status,
          },
          headers: this.sessionRepository.getAuthenticationHeader(),
        },
      };
    });

    const response = await this.requests.multipleAsyncWait({
      requests,
    });

    const updatedFlatPlan = response.flat(1) as PlanDTO[];

    return updatedFlatPlan
      .filter((flatPlan) => flatPlan !== null)
      .map((flatPlan: PlanDTO) => PlanFlatMapper.toDomain(flatPlan));
  }

  async deleteFlatPlans(flatPlans: Array<PlanFlat>): Promise<void> {
    const requests = flatPlans.map((flatPlan) => {
      return {
        method: this.requests.delete.bind(this.requests),
        params: {
          uri: `/v2/plan/${flatPlan.id}`,
          headers: this.sessionRepository.getAuthenticationHeader(),
        },
      };
    });

    await this.requests.multipleAsyncWait({ requests });
  }

  async duplicatePlan({
    sourcePlan,
    duplicates,
  }: {
    sourcePlan: PlanFlat;
    duplicates: { label: string; status: PlanStatus; execution_date: Date }[];
  }): Promise<Array<PlanFlat | null>> {
    const requests = duplicates.map(({ label, status, execution_date }) => {
      return {
        method: this.requests.post.bind(this.requests),
        params: {
          uri: `/v2/plan/${sourcePlan.id}/duplicate`,
          data: {
            label,
            status,
            execution_date,
          },
          headers: this.sessionRepository.getAuthenticationHeader(),
        },
      };
    });

    const response = await this.requests.multipleAsyncWait({
      requests,
    });

    const flatPlans = response.flat(1) as PlanFlatDTO[];

    return flatPlans.map((flatPlan: PlanFlatDTO | null) => {
      return flatPlan !== null ? PlanFlatMapper.toDomain(flatPlan) : null;
    });
  }

  async getPlanDrivers({ planId, project }: { planId: string; project: Project }): Promise<Array<Driver>> {
    const response = await this.requests.get({
      uri: `/v2/plan/${planId}/drivers`,
      headers: this.sessionRepository.getAuthenticationHeader(),
    });

    const drivers = response.data as Array<DriverDTO>;

    return drivers.map((driver) => DriverMapper.toDomain(driver, project.custom_fields!));
  }

  async getPlanStops({ planId, project }: { planId: string; project: Project }): Promise<Array<Stop>> {
    const response = await this.requests.get({
      uri: `/v2/plan/${planId}/stops`,
      headers: this.sessionRepository.getAuthenticationHeader(),
    });

    const stops = response.data as Array<StopDTO>;

    return stops.map((stop) => StopMapper.toDomain(stop, project.custom_fields!));
  }

  async getPlanOptimization({ planId }: { planId: string }): Promise<PlanOptimizationData> {
    const response = await this.requests.get({
      uri: `/v2/plan/${planId}/running_optimization`,
      headers: this.sessionRepository.getAuthenticationHeader(),
    });

    if (response.status === 204) {
      console.info(`getPlanOptimization response 204`);
      return {
        status: undefined,
        createdAt: undefined,
      };
    }

    console.info(`getPlanOptimization response 200`);
    return {
      status: response.data.status,
      createdAt: response.data.created_at,
    };
  }

  async getPlanGeoFences({ planId }: { planId: string }): Promise<Array<GeoFence>> {
    const response = await this.requests.get({
      uri: `/v2/plan/${planId}/geo_fences`,
      headers: this.sessionRepository.getAuthenticationHeader(),
    });

    const geoFences = response.data as Array<GeoFenceDTO>;

    return geoFences.map(GeoFenceMapper.toDomain);
  }

  async optimize({
    plan,
    project,
    keepCurrentAssignment,
  }: {
    plan: PlanFlat;
    project: Project;
    keepCurrentAssignment: boolean;
  }): Promise<Plan> {
    const params = new URLSearchParams();
    params.append(`keep_current_assignment`, `${keepCurrentAssignment}`);

    const response = await this.requests.post({
      uri: `/v2/plan/${plan.id}/optimize?${params.toString()}`,
      headers: this.sessionRepository.getAuthenticationHeader(),
    });

    const updatedPlan = response.data as PlanDTO;

    return PlanMapper.toDomain(updatedPlan, project.custom_fields!);
  }

  async getPlansCalendar({
    startDate,
    endDate,
    projectId,
  }: {
    startDate: Date;
    endDate: Date;
    projectId: string;
  }): Promise<Array<PlansCalendar>> {
    const response = await this.requests.get({
      uri: `/v2/plans/calendar`,
      params: {
        start_date: startDate,
        end_date: endDate,
        project_id: projectId,
      },
      headers: this.sessionRepository.getAuthenticationHeader(),
    });
    const data = response.data as PlansCalendarDTO[];

    return data.map(PlansCalendar.create);
  }
}
